import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as ol from 'openlayers'
import 'ol/ol.css'
import { DrawEvent } from 'ol/src/interaction/Draw'
import { SelectEvent } from 'ol/src/interaction/Select'
import proj4 from 'proj4'

import { featureCollection, lineString } from 'turf'

import { GeoLayer } from '../../dataSources/layers/Layer'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import {
  addLayerByType,
  removeLayerByType,
  getGeoLayerByType,
  LayerParams,
  LayerCapabilities,
  LayerType,
  getLayerDescriptionForType,
  LayerCopyright,
  LayerConstructionOptions
} from '../../dataSources/layers/LayerRegistry'
import { RootState } from '../../Store'
import { DataTheme, ToolType } from 'src/Enums'
import { getLayerCapabilities, getSubLayersFromCapabilities } from 'src/utils/MapLayerUtils'
import { numberToDecimalString, rgbToHex } from 'src/utils/Utils'

import { ColorInFocus, mapActions } from 'src/actions/MapActions'
import { profileActions } from 'src/actions/ProfileActions'
import { getLayerCapabilitiesFromType, getLayerParamsFromType } from 'src/reducers/MapReducer'
import { ProfileView, ProfileSetting } from 'src/reducers/ProfileReducer'
import { ProfileObject } from 'src/dataSources/geo/CrossSection'

import { openLayersMapStyle, OpenLayersMapStyleProps } from './OpenLayersMap.style'
import { generalActions } from 'src/actions/GeneralActions'
import { getThemeOptionsByDataTheme } from 'src/Themes'
import { createMuiTheme } from '@material-ui/core'
import { wikiActions } from 'src/actions/WikiActions'
import { infoMenuActions } from 'src/actions/InfoMenuActions'
import { InfoView } from 'src/reducers/InfoMenuReducer'
import { toLonLat } from 'ol/proj'
import { getDistance } from 'ol/sphere'
import { Graph } from 'src/dataSources/graphs/GraphRegistry'
import { Extent } from 'openlayers'
import { WikiLocation } from 'src/dataSources/wiki/Wiki'

let olMap: any = null
let olMapMoveEndListener: any = null
let olPointerMoveListener: any = null
let olMapClickListener: any = null
const profileToolLineSource: ol.source.Vector = new ol.source.Vector()
const profileToolPointSource: ol.source.Vector = new ol.source.Vector()
const profileToolLineBufferSource: ol.source.Vector = new ol.source.Vector()
const profileToolLineLayer: ol.layer.Vector = new ol.layer.Vector()
const profileToolPointLayer: ol.layer.Vector = new ol.layer.Vector()
const profileToolLineBufferLayer: ol.layer.Vector = new ol.layer.Vector()
let lastZoomLevel = 15
let olDraw: ol.interaction.Draw
let olModify: ol.interaction.Modify
let olSnap: ol.interaction.Snap
let olSelect: ol.interaction.Select

interface Props {
  zoomLevel: number
  layerTypes: LayerType[]
  onLeftClick: (coord: number[]) => void
}

const defaultProps = {
  zoomLevel: lastZoomLevel,
  layerTypes: [],
  onLeftClick: (coord: number[]): void => { }
}

/**
 * OpenLayersMap React component.
 * @param {object} props React properties object.
 * The following properties are implemented:
 * zoomLevel {number}: The initial zoom level if the map component.
 * layers {array}: The layers to add to the map. Must all be subclasses
 * of the @see Layer class.
 * @returns React view components.
 */
function OpenLayersMap (props: Props): JSX.Element {
  const dispatch = useDispatch()

  const mapDiv: any = useRef()
  const [map, setMap] = useState<ol.Map | undefined>()
  const [currentMouseCoordinates, setCurrentMouseCoordinates] = useState<number[]>([0, 0])
  const [layerCopyrightMap, setLayerCopyrightMap] = useState<Map<string, LayerCopyright>>(new Map())

  const mapCenter = useSelector((state: RootState): ol.Coordinate => state.mapState.mapCenter)
  const mapCurrentZoom = useSelector((state: RootState): number => state.mapState.mapCurrentZoom)
  const profileCoordinates = useSelector((state: RootState): ol.Coordinate[] | undefined => state.profileState.coordinates)
  const crosshairDistance = useSelector((state: RootState): number | undefined => state.profileState.crosshairDistance)
  const currentDataTheme: DataTheme = useSelector((state: RootState): DataTheme => state.generalState.dataTheme)
  const layerParams = useSelector((state: RootState): LayerParams[] => state.mapState.layerParams)
  const modifiedLayer = useSelector((state: RootState): LayerType|undefined => state.mapState.modifiedLayer)
  const layerCapabilities = useSelector((state: RootState): LayerCapabilities[] => state.mapState.layerCapabilities)
  const maxBoreholeDistance = useSelector((state: RootState): number => state.profileState.maxBoreholeDistance)
  const enabledProfileSettings = useSelector((state: RootState): ProfileSetting[] => state.profileState.enabledSettings)
  const models = useSelector((state: RootState): GeoModel[] | undefined => state.generalState.geoModels)
  const poi = useSelector((state: RootState): WikiLocation[] | undefined => state.wikiState.poi)
  const selectedGeoModel = useSelector((state: RootState): GeoModel | undefined => state.generalState.selectedGeoModel)
  const selectedProfile = useSelector((state: RootState): ProfileObject | undefined => state.profileState.selectedProfile)
  const selectedTool = useSelector((state: RootState): ToolType => state.generalState.selectedTool)
  const tokenSelector = useSelector((state: RootState) => state.authState.token)
  const bottomBarHeight = useSelector((state: RootState) => state.generalState.bottomBarHeight)
  const showLeftSideBar = useSelector((state: RootState): boolean => state.generalState.showLeftSideBar)
  const showWikiSideBar = useSelector((state: RootState): boolean => state.wikiState.showWikiSideBar)
  const currentInfoView = useSelector((state: RootState): InfoView => state.infoMenuState.currentView)
  const rightSideBarOpen: boolean = showWikiSideBar || currentInfoView !== InfoView.Empty
  const rightSideBarWidth = useSelector((state: RootState): number => state.generalState.rightSideBarWidth)
  const currentBottomView = useSelector((state: RootState): ProfileView => state.profileState.currentView)
  const selectedGraph = useSelector((state: RootState): Graph | undefined => state.generalState.selectedGraph)
  const colorsInFocus = useSelector((state: RootState): ColorInFocus[] => state.mapState.colorsInFocus)

  const bottomBarOpen: boolean = selectedGraph !== undefined || currentBottomView !== ProfileView.Empty

  const olmsProps: OpenLayersMapStyleProps = {
    bottomBarOpen: bottomBarOpen,
    bottomBarHeight: bottomBarHeight,
    rightSideBarWidth: rightSideBarWidth
  }
  const classes = openLayersMapStyle(olmsProps)

  /**
   * Adds layers to the map.
   * If a layer needs capabilities, these are fetched from the supplied capabilitiesUrl.
   */
  const addLayers = async (layersToAdd: LayerType[]): Promise<void> => {
    const copyrightMap = new Map()
    const geoLayerPromises = layersToAdd.map(async layerType => {
      const layerDescription = getLayerDescriptionForType(layerType)

      copyrightMap.set(layerDescription.layerCopyright.name, layerDescription.layerCopyright)

      let params = getLayerParamsFromType(layerType, layerParams)
      params = params !== undefined ? params : {}
      const options: LayerConstructionOptions = {
        token: tokenSelector,
        selectedGeoModel: selectedGeoModel !== undefined ? selectedGeoModel.ID : '',
        geoModels: models,
        capabilities: getLayerCapabilitiesFromType(layerType, layerCapabilities),
        params: params,
        poi: poi
      }

      if (layerDescription.capabilitiesUrl !== '' && options.capabilities === undefined) {
        const capabilities = await getLayerCapabilities(layerType, layerDescription.capabilitiesUrl, layerCapabilities)
        if (capabilities !== undefined) {
          options.capabilities = capabilities

          const subLayers = getSubLayersFromCapabilities(capabilities)
          if (params.subLayers !== undefined) {
            params.subLayers.list = subLayers
            if (subLayers.length > 0) {
              params.subLayers.selected.subLayer = subLayers[0]
            }
          }

          dispatch(mapActions.setLayerCapabilities({
            type: layerType,
            capabilities: capabilities
          }))
          dispatch(mapActions.setLayerParams({
            layerType: layerType,
            params: params
          }))
        }
      }
      return addLayerByType(layerType, options)
    })

    const geoLayers = await Promise.all(geoLayerPromises)
    
    // Add layers
    const currentZoomLevel = olMap.getView().getZoom()
    for (const layer of geoLayers) {
      const olLayer = layer.olLayer
      if (currentZoomLevel < layer.getMinimumZoom()) {
        olLayer.setVisible(false)
      }
      olMap.addLayer(olLayer)
    }

    // Make sure we have the interaction we need for the feature layers.
    enableFeatureLayerInteraction()

    // Update copyright info.
    setLayerCopyrightMap(copyrightMap)
  }

  const removeLayers = (layersToRemove: LayerType[]): boolean => {
    // Remove layers from the actual map component.
    const layers = olMap
      .getLayers()
      .getArray()
      .filter((l: ol.layer.Layer) => layersToRemove.some(x => x === l.get('geo_type')))
    for (const l of layers) {
      olMap.removeLayer(l)
    }

    // Remove layers from the registry's list of active layers.
    // This is mostly just a resource consumption issue - keep unused layers out of memory.
    let didRemoveLayer = false
    for (const l of layersToRemove) {
      if (removeLayerByType(l)) {
        didRemoveLayer = true
      }
    }

    // Handle interaction for the model area layer if removed.
    if (layersToRemove.some(l => l === LayerType.MODEL_AREAS)) {
      clearFeatureLayerInteractions()
    }

    return didRemoveLayer
  }

  /**
    * Calculates a buffer for input features for a given radius. Units supported are miles, kilometers, and degrees.
    * Adopted from Turf.js.
    * @param feature Input to be buffered
    * @param radius Radius to draw the buffer
    * @param units 'miles', 'kilometers', 'radians', or 'degrees'
    * @returns Buffered features
    */
  const buffer = (feature: GeoJSON.FeatureCollection<any, GeoJSON.GeoJsonProperties>, radius: number, units: typeof TemplateUnits): GeoJSON.FeatureCollection<GeoJSON.Polygon> | undefined => {
    // eslint-disable-next-line
    const helpers = require('turf-helpers')
    // eslint-disable-next-line
    const normalize = require('geojson-normalize')

    const featureCollection = helpers.featureCollection
    const degrees = helpers.distanceToDegrees(radius, units)
    const fc = normalize(feature)
    const buffered = normalize(featureCollection(fc.features.map(function (f: any) {
      return bufferOp(f, degrees)
    })))

    if (buffered.features.length > 1) return buffered
    else if (buffered.features.length === 1) return buffered.features[0]
    return undefined
  }

  /**
   * Perform buffer operation with JSTS.
   * @param feature Feature to buffer.
   * @param radius Radius to draw around the feature.
   * @returns Buffered feature.
   */
  const bufferOp = (feature: any, radius: number): any => {
    // eslint-disable-next-line
    const jsts = require('jsts')

    const reader = new jsts.io.GeoJSONReader()
    const geom = reader.read(feature.geometry)
    const writer = new jsts.io.GeoJSONWriter()
    let buffered = geom.buffer(radius, jsts.operation.buffer.BufferParameters.DEFAULT_QUADRANT_SEGMENTS, jsts.operation.buffer.BufferParameters.CAP_FLAT)
    buffered = writer.write(buffered)

    return {
      type: 'Feature',
      geometry: buffered,
      properties: {}
    }
  }

  /**
   * Clear feature layer interactions.
   */
  const clearFeatureLayerInteractions = (): void => {
    olMap.getInteractions().forEach((interaction: ol.interaction.Interaction) => {
      if (interaction instanceof ol.interaction.Select) {
        interaction.setActive(false)
        olMap.removeInteraction(interaction)
      }
    })
  }

  /**
   * Clear draw, modify and snap interactions on map.
   */
  const clearProfileToolInteractions = (): void => {
    olMap.getInteractions().forEach((interaction: ol.interaction.Interaction) => {
      if (interaction instanceof ol.interaction.Draw || interaction instanceof ol.interaction.Modify || interaction instanceof ol.interaction.Snap) {
        interaction.setActive(false)
        olMap.removeInteraction(interaction)
      }
    })
  }

  /**
   * Clear any features in the sources.
   */
  const clearSources = (): void => {
    profileToolLineSource.clear()
    profileToolPointSource.clear()
    profileToolLineBufferSource.clear()
  }

  /**
   * Disable drawing and modifying tools for profile tool.
   */
  const disableProfileTool = (): void => {
    clearSources()
    clearProfileToolInteractions()
  }

  /**
   * Get map extent of the current viewport.
   * @returns Extent object.
   */
  const getMapExtent = (): Extent => {
    return olMap.getView().calculateExtent(olMap.getSize()) as Extent
  }

  /**
   * Return a style for a given feature.
   * @param feature Feature object.
   * @param resolution Resolution.
   * @returns Style object.
   */
  const pointStyleFunction = (feature: ol.Feature | ol.render.Feature, resolution: number): (ol.style.Style | ol.style.Style[] | null) => {
    const theme = createMuiTheme(getThemeOptionsByDataTheme(currentDataTheme))
    return new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 1.0)'
      }),
      stroke: new ol.style.Stroke({
        color: theme.palette.primary.main,
        width: 4
      }),
      image: new ol.style.Circle({
        radius: 4,
        fill: new ol.style.Fill({
          color: theme.palette.primary.main
        })
      }),
      text: new ol.style.Text({
        font: '23px Calibri,sans-serif',
        offsetY: 18,
        offsetX: feature.get('name') === 'A\'' ? 3 : 0,
        fill: new ol.style.Fill({ color: '#000' }),
        stroke: new ol.style.Stroke({
          color: '#fff', width: 4
        }),
        text: olMap.getView().getZoom() > 9 ? feature.get('name') : ''
      })
    })
  }

  const xStyleFunction = (feature: ol.Feature | ol.render.Feature, resolution: number): (ol.style.Style | ol.style.Style[] | null) => {
    return new ol.style.Style({
      text: new ol.style.Text({
        font: '18px Calibri,sans-serif',
        offsetY: 1,
        fill: new ol.style.Fill({ color: '#F00' }),
        stroke: new ol.style.Stroke({
          color: '#fff', width: 2
        }),
        text: olMap.getView().getZoom() > 9 ? feature.get('name') : ''
      })
    })
  }

  /**
   * Enable GeoModel selection by clicking the map
   * @param layer the layer with the model features
   */
  const enableFeatureLayerInteraction = (): void => {
    // Clear any existing interaction.
    clearFeatureLayerInteractions()

    // Find layers that need this interaction.
    const layerTypes = [LayerType.MODEL_AREAS, LayerType.COSTAL_LOCATIONS]
    const layers: ol.layer.Layer[] = olMap.getLayers().getArray().filter((l: ol.layer.Layer) => layerTypes.includes(l.get('geo_type')))

    if (layers.length > 0) {
      olSelect = new ol.interaction.Select({
        layers: layers,
        hitTolerance: 5
      })
      olSelect.on('select', (event) => {
        const feature = (event as SelectEvent).selected.find(f => f)
        if (feature !== undefined) {
          if (feature.get('type') === 'model_area') {
            const selectedGeoModel = models?.find(model => model.ID === feature.get('id'))
            if (selectedGeoModel !== undefined) {
              dispatch(generalActions.setSelectedGeoModel(selectedGeoModel))
            }
          }
          if (feature.get('type') === 'wiki_link') {
            dispatch(wikiActions.fetchDocument(feature.get('key')))
          }
        }
      })
      olMap.addInteraction(olSelect)
    }
  }

  /**
   * Enable drawing and modifying tools for profile tool.
   */
  const enableProfileTool = (): void => {
    clearSources()
    if (profileCoordinates !== undefined) {
      profileToolLineSource.addFeature(new ol.Feature({
        geometry: new ol.geom.LineString(profileCoordinates),
        name: 'Line'
      }))
    }

    const theme = createMuiTheme(getThemeOptionsByDataTheme(currentDataTheme))
    const lineStyle = new ol.style.Style({
      fill: new ol.style.Fill({
        color: 'rgba(255, 255, 255, 1.0)'
      }),
      stroke: new ol.style.Stroke({
        color: theme.palette.primary.main,
        width: 4
      }),
      image: new ol.style.Circle({
        radius: 7,
        fill: new ol.style.Fill({
          color: theme.palette.primary.main
        })
      })
    })
    profileToolLineLayer.setStyle(lineStyle)
    profileToolLineLayer.setZIndex(Number.MAX_SAFE_INTEGER)
    profileToolLineLayer.setSource(profileToolLineSource)

    if (profileCoordinates !== undefined) {
      const showProfileBuffer = enabledProfileSettings.some(enabledSetting => enabledSetting === ProfileSetting.SHOW_PROFILE_BUFFER)
      if (showProfileBuffer) {
        // Add buffer to profile line
        const line = lineString(profileCoordinates)
        const format = new ol.format.GeoJSON()

        // For some reason 100000 = 1000 meters
        const buffered = buffer(featureCollection([line]), 100000 * ((2 * maxBoreholeDistance) / 1000), 'kilometers')
        if (buffered !== undefined) {
          const bufferFeature = format.readFeature(buffered)
          profileToolLineBufferSource.addFeature(bufferFeature)
          const lineStyle = new ol.style.Style({
            fill: new ol.style.Fill({
              color: 'rgba(255, 255, 255, 0.4)'
            }),
            stroke: new ol.style.Stroke({
              color: theme.palette.primary.main,
              width: 0.5
            })
          })
          profileToolLineBufferLayer.setStyle(lineStyle)
          profileToolLineBufferLayer.setZIndex(Number.MAX_SAFE_INTEGER - 1)
          profileToolLineBufferLayer.setSource(profileToolLineBufferSource)
        }
      }

      // Draw points of profile
      const features = []
      let index = 0
      for (const coordinate of profileCoordinates) {
        features.push(new ol.Feature({
          geometry: new ol.geom.Point(coordinate),
          name: index === 0 ? 'A' : (index === profileCoordinates.length - 1 ? 'A\'' : '')
        }))
        index++
      }

      profileToolPointSource.addFeatures(features)
      profileToolPointLayer.setStyle(pointStyleFunction)
      profileToolPointLayer.setSource(profileToolPointSource)
      profileToolPointLayer.setZIndex(Number.MAX_SAFE_INTEGER - 1)
    }

    // Clear existing interactions
    clearProfileToolInteractions()

    // Create new interactions
    olDraw = new ol.interaction.Draw({
      source: profileToolLineSource,
      type: 'LineString'
    })
    olDraw.on('drawstart', (event) => {
      clearSources()
      dispatch(profileActions.currentView(ProfileView.Empty))
    })
    olDraw.on('drawend', (event) => {
      const coordinates: ol.Coordinate[] = (event as DrawEvent).feature.getGeometry().getCoordinates()
      dispatch(wikiActions.clearViews())
      dispatch(infoMenuActions.currentView(InfoView.Empty))
      dispatch(generalActions.setShowLeftSideBar(false))
      dispatch(profileActions.setCoordinates(coordinates))
      dispatch(profileActions.fetchProfile())
      dispatch(profileActions.currentView(ProfileView.Info))
    })
    olMap.addInteraction(olDraw)

    olSnap = new ol.interaction.Snap({ source: profileToolLineSource })
    olMap.addInteraction(olSnap)

    olModify = new ol.interaction.Modify({ source: profileToolLineSource })
    olModify.on('modifyend', (event) => {
      const features = profileToolLineSource.getFeatures()
      features.forEach((feature) => {
        const point: ol.geom.Point = feature.getGeometry() as ol.geom.Point
        if (point !== undefined) {
          // TODO: For some reason the type does not match the actual object returned - this is the only solution I have found
          // This object is actually a list of points
          const coordinates: ol.Coordinate[] = (point.getCoordinates() as unknown) as ol.Coordinate[]
          dispatch(profileActions.setCoordinates(coordinates))
          dispatch(profileActions.fetchProfile())
        }
      })
    })
    olMap.addInteraction(olModify)
  }

  // Helper method for registering the left click handler on the map.
  const registerClickListener = (): void => {
    // Unregister any existing click listener so we don't end up
    // having multiple.
    if (olMapClickListener !== null) {
      olMap.un('click', olMapClickListener.listener)
    }
    // Register new click listener.
    olMapClickListener = olMap.on('click', (event: ol.MapBrowserEvent) => {
      const clickedCoordinate = event.map.getCoordinateFromPixel(event.pixel)
      props.onLeftClick(clickedCoordinate)
    })
  }

  // Helper method for registering the 'moveend' handler on the map.
  const registerMoveEndListener = (): void => {
    // Handler for map move events.
    // Make sure to unregister any existing 'moveend' listener.
    if (olMapMoveEndListener !== null) {
      olMap.un('moveend', olMapMoveEndListener.listener)
    }
    // Add new 'moveend' listener.
    olMapMoveEndListener = olMap.on('moveend', (event: ol.MapBrowserEvent) => {
      const currentZoomLevel = event.map.getView().getZoom()
      if (currentZoomLevel !== lastZoomLevel) {
        // We need to advise enabled layers that zoom level has changed.
        // We find the enabled layers by looking at what is in the map right now.
        const layers: LayerType[] = event.map.getLayers().getArray().map(l => l.get('geo_type') as LayerType).filter(x => x !== undefined)

        // Now update the zoom level.
        lastZoomLevel = currentZoomLevel
        const geoLayers = layers.map(layerType => getGeoLayerByType(layerType))
        geoLayers.forEach((layer: GeoLayer) => {
          layer.onZoomChange(currentZoomLevel)
        })
      }

      // Update state
      dispatch(mapActions.setMapExtent(getMapExtent()))
      dispatch(mapActions.setMapCurrentZoom(currentZoomLevel))
      dispatch(mapActions.setMapCenter(event.map.getView().getCenter()))
    })
  }

  // Helper method for registering the 'pointermove' handler on the map.
  const registerPointerMoveListener = (): void => {
    // Handler for mouse move events.
    // Make sure to unregister any existing 'pointermove' listener.
    if (olPointerMoveListener !== null) {
      olMap.un('pointermove', olPointerMoveListener.listener)
    }
    // Add new 'pointermove' listener.
    olPointerMoveListener = olMap.on('pointermove', (event: any) => {
      setCurrentMouseCoordinates(event.coordinate)
      const newColorsInFocus: ColorInFocus[] = []
      return;
      event.map.forEachLayerAtPixel(event.pixel, (layer: ol.layer.Layer, color: ol.Color) => {
        const layerType = layer.get('geo_type') as LayerType
        if (layerType !== undefined) {
          const layerDescriptor = getLayerDescriptionForType(layerType)
          if (layerDescriptor.legendEnabled && color !== null) {
            const hexColor = rgbToHex(color[0], color[1], color[2])
            newColorsInFocus.push({
              layerType: layerType,
              color: hexColor
            })
          }
        }
      })
 
      // Dispatch if anything has changed.
      if (newColorsInFocus.length !== colorsInFocus.length) {
        // New array length -> a change occurred.
        dispatch(mapActions.setColorsInFocus(newColorsInFocus))
      } else {
        // Same length -> compare values.
        let allFound = true;
        colorsInFocus.forEach((c1: ColorInFocus) => {
          const idx = newColorsInFocus.findIndex((c2: ColorInFocus) => c1.color === c2.color && c1.layerType === c2.layerType)
          if (idx === -1) {
            allFound = false;
          }
        })
        if (!allFound) {
          dispatch(mapActions.setColorsInFocus(newColorsInFocus))
        }
      }

      let foundFeature = false;
      event.map.forEachFeatureAtPixel(event.pixel, (feature: ol.Feature, layer: ol.layer.Layer) => {
        foundFeature = true;
      })
      event.map.getViewport().style.cursor = foundFeature ? 'pointer' : '';
    })
  }

  const getControlsStyling = (): string => {
    let style = ''
    style += showLeftSideBar ? ` ${classes.leftSideBarOpen}` : ` ${classes.leftSideBarClosed}`
    style += rightSideBarOpen ? ` ${classes.rightSideBarOpen}` : ` ${classes.rightSideBarClosed}`
    style += bottomBarOpen ? ` ${classes.bottomBarOpen}` : ` ${classes.bottomBarClosed}`
    return style
  }

  /**
   * Transforms coordinate from EPSG:25832 to EPSG:3857
   * @param coordinate coordinate to be transformed
   * @returns transformed coordinate
   */
  const toEPSG3857 = (coordinate: ol.Coordinate): ol.Coordinate => {
    return ol.proj.transform(coordinate, 'EPSG:25832', 'EPSG:3857')
  }

  /**
   * Calculates and returns the Point at which to place the crosshair marker.
   * @param coordinates The coordinates defining the line segments on which to place the marker.
   * @param crossDistance The distance from the first coordinates/Point to place the marker
   * @returns The Point at which to place the crosshair marker or undefined if the Point couldn't be calculated.
   */
  const calculateCrosshairPoint = (coordinates: ol.Coordinate[], crossDistance: number): ol.geom.Point | undefined => {
    let crosshairPoint: ol.geom.Point | undefined
    let startNode: ol.Coordinate | undefined
    let endNode: ol.Coordinate | undefined

    let accumulatedDistance = 0
    let crosshairOffset = 0
    let startEndNodeDistance = 0

    let index = 0
    while (accumulatedDistance < crossDistance && index < coordinates.length - 1) {
      const oldDistance = accumulatedDistance
      const distance = getDistance(toLonLat(toEPSG3857(coordinates[index])), toLonLat(toEPSG3857(coordinates[index + 1])))
      accumulatedDistance += distance
      if (accumulatedDistance >= crossDistance) {
        startNode = coordinates[index]
        endNode = coordinates[index + 1]
        crosshairOffset = crossDistance - oldDistance
        startEndNodeDistance = distance
        break
      }
      index++
    }

    if (startNode !== undefined && endNode !== undefined) {
      const scaleFactor = crosshairOffset / startEndNodeDistance
      const deltaX = (endNode[0] - startNode[0]) * scaleFactor
      const deltaY = (endNode[1] - startNode[1]) * scaleFactor
      crosshairPoint = new ol.geom.Point(startNode)
      crosshairPoint.translate(deltaX, deltaY)
    }

    return crosshairPoint
  }

  // On mount run this method that initializes the base map.
  useEffect(() => {
    const northArrowSpan = document.createElement('span')
    northArrowSpan.innerHTML = `<img src="north-icon.svg" class="${classes.northIcon}">`

    // Register projection to EPSG:25832
    const projectionName = 'EPSG:25832'
    proj4.defs(projectionName, '+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs')
    ol.proj.setProj4(proj4)

    if (map !== undefined) {
      olMap = map
    } else {
      const olLayers: ol.layer.Layer[] = []
      olMap = new ol.Map({
        target: mapDiv.current,
        layers: olLayers,
        view: new ol.View({
          center: mapCenter,
          zoom: mapCurrentZoom,
          minZoom: 7.9, // This value allows for the entire ice age layer to be visible.
          projection: projectionName
        }),
        controls: [
          new ol.control.Rotate({ autoHide: false, label: northArrowSpan }),
          new ol.control.Zoom(),
          new ol.control.ScaleLine()
        ]
      })
      setMap(olMap)

      // Add vector layers used by the profile tool.
      olMap.addLayer(profileToolLineLayer)
      olMap.addLayer(profileToolLineBufferLayer)
      olMap.addLayer(profileToolPointLayer)

      // Set current map extent
      dispatch(mapActions.setMapExtent(getMapExtent()))

      // Register event listeners.
      registerMoveEndListener()
      registerPointerMoveListener()
      registerClickListener()
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    olMap.getView().setCenter(mapCenter)
    olMap.getView().setZoom(mapCurrentZoom)
  }, [mapCenter, mapCurrentZoom])

  useEffect(() => {
    enableProfileTool()
    // eslint-disable-next-line
  }, [maxBoreholeDistance, enabledProfileSettings])

  useEffect(() => {
    const vectorMarkerName = 'crosshairMark'
    olMap.getLayers().getArray()
      .filter((layer: any) => layer.get('name') === vectorMarkerName)
      .forEach((layer: any) => {
        olMap.removeLayer(layer)
      })

    if (profileCoordinates !== undefined && crosshairDistance !== undefined) {
      const crosshairPoint = calculateCrosshairPoint(profileCoordinates, crosshairDistance)
      if (crosshairPoint !== undefined) {
        setCurrentMouseCoordinates(crosshairPoint.getCoordinates())
      }

      const olSourceMarkerPoint = new ol.source.Vector({
        features: [new ol.Feature({
          geometry: crosshairPoint,
          name: 'X'
        })]
      })
      const olVectorMarkerPoint = new ol.layer.Vector({
        source: olSourceMarkerPoint,
        style: xStyleFunction
      })
      olVectorMarkerPoint.setZIndex(Number.MAX_SAFE_INTEGER)
      olVectorMarkerPoint.set('name', vectorMarkerName)
      olMap.addLayer(olVectorMarkerPoint)
    }
    // eslint-disable-next-line
  }, [crosshairDistance, profileCoordinates])

  // This useEffect call should only be invoked upon changes to the
  // list of available layers.
  useEffect(() => {
    // Layers that the registry says should be running.
    const layersToEnable = props.layerTypes

    // Layers that are actually running within the OpenLayer map.
    const currentlyEnabledLayers: LayerType[] =
      olMap
        .getLayers()
        .getArray()
        .map((l: ol.layer.Layer) => l.get('geo_type') as LayerType)
        .filter((t: LayerType) => t !== undefined)

    // Find layers that should be added or removed.
    const addTheseLayers: LayerType[] = []
    const removeTheseLayers: LayerType[] = []
    for (const l of layersToEnable) {
      if (!currentlyEnabledLayers.some(x => x === l)) {
        addTheseLayers.push(l)
      }
    }
    for (const l of currentlyEnabledLayers) {
      if (!layersToEnable.some(x => x === l)) {
        removeTheseLayers.push(l)
      }
    }

    // Modify the layers being shown within the map component.
    if (addTheseLayers.length > 0) {
      addLayers(addTheseLayers)
        .catch(e => console.error('Adding layers to map component', e))
    }
    if (removeTheseLayers.length > 0) {
      removeLayers(removeTheseLayers)
    }
  }, [props.layerTypes])

  // Handle changes in selected geo model.
  useEffect(() => {
    const layersTypesToUpdate = [LayerType.MODEL_AREAS, LayerType.SLICE_DHM, LayerType.SLICE_KOTE]
    layersTypesToUpdate.forEach(layerType => {
      const layer = olMap.getLayers().getArray().find((l: ol.layer.Layer) => l.get('geo_type') === layerType)
      if (layer !== undefined) {
        removeLayers([layerType])
        addLayers([layerType])
          .catch(e => console.error('Reloading layer because of Geo model change', e))
      }
    })
  }, [selectedGeoModel, models])

  useEffect(() => {
    const layer = olMap.getLayers().getArray().find((l: ol.layer.Layer) => l.get('geo_type') === LayerType.COSTAL_LOCATIONS)
    if (layer !== undefined) {
      removeLayers([LayerType.COSTAL_LOCATIONS])
      addLayers([LayerType.COSTAL_LOCATIONS])
        .catch(e => console.error('Reloading layer because of state change', e))
    }
  }, [poi])

  // Handle changes in selected tool.
  useEffect(() => {
    // Initialize profile tool
    if (selectedTool === ToolType.PROFILE) {
      enableProfileTool()
    } else {
      disableProfileTool()
    }
  }, [selectedTool, selectedProfile])

  // Handle changes in layer parameters. Only one layer may change parameters at the time
  // in the state. This layer will be named in modifiedLayer.
  useEffect(() => {
    if (modifiedLayer !== undefined) {
      // Find the modified layer and re-draw it.
      if (removeLayers([modifiedLayer])) {
        // If a layer was in fact removed, add it again.
        addLayers([modifiedLayer])
          .catch(e => console.error('Updating layer parameters', e))
      }
    }
  }, [layerParams])

  return (
    <div ref={mapDiv} onMouseLeave={() => dispatch(mapActions.setColorsInFocus([]))} className={`${classes.root}${getControlsStyling()}`}>
      <div id='copyrightWrapper' className={`${classes.copyrightWrapper} ${getControlsStyling()}`}>
        <p className={classes.copyright}>
          {Array.from(layerCopyrightMap.values())
            .sort((i1, i2) => i1.priority - i2.priority)
            .map((item, index) => {
              return (
                <a className={classes.copyrightItem} key={index} href={item.url} target='_blank' rel='noreferrer'>© {item.name}</a>
              )
            })}
        </p>
      </div>
      <div id='mousePositionWrapper' className={`${classes.mousePositionWrapper} ${getControlsStyling()}`}>
        <p className={classes.mousePosition}>
          E {numberToDecimalString(currentMouseCoordinates[0])} N {numberToDecimalString(currentMouseCoordinates[1])}
        </p>
        <p className={`${classes.mousePosition} ${classes.mousePositionCoordinateSystem}`}>(EPSG:25832)</p>
      </div>
    </div>
  )
}

OpenLayersMap.defaultProps = defaultProps

export default OpenLayersMap
